/**
 * @file 知识库相关 API Hooks（列表 + 创建知识库 + 创建文档 + 团队列表）
 *
 * Layer 3 数据接入：请求与类型内聚在此文件，不单独建 Agent/services。
 * - 获取知识库列表（含文档合并）
 * - 创建知识库（POST /api/kb/v1/knowledge-bases）
 * - 添加文档（POST /api/kb/v1/knowledge-bases/{kbId}/knowledge/manual）
 * - 获取用户团队列表（GET /api/v1/auth/me → memberships）
 */
import { useCallback, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import http from '@/utils/http';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController';
import {
  normalizeKbList,
  getKnowledgeBaseListCacheKey,
} from '../utils/knowledgeBaseUtils';

// ==============================
// 类型定义
// ==============================

/** 知识库列表项 */
export interface IKnowledgeBaseItem {
  /** 知识库 ID */
  kbId: string;
  /** 知识库名称 */
  kbName: string;
  /** 知识库类型（document / faq） */
  type?: 'document' | 'faq';
  /** 所属团队 ID */
  tenantId?: string;
  /** 文档数量 */
  docCount?: number;
  /** 时间段（上层传入，如「最近一周」） */
  timeRange?: string;
  /** 最后更新时间（ISO 8601，来自接口 updated_at） */
  updatedAt?: string;
  /** 知识库下的文档；undefined 表示尚未按需加载，[] 表示已加载但为空 */
  docs?: IKnowledgeDocItem[];
}

/** 知识库文档列表项 */
export interface IKnowledgeDocItem {
  /** 文档 ID */
  docId: string;
  /** 文档展示名（title > display_file_name > source） */
  docName: string;
  /** 所属知识库 ID */
  kbId: string;
  /** 文档类型图标 URL */
  docIcon?: string;
  /** 来源渠道（tapd / wiki / link / video / image 等） */
  channel?: string;
  /** 文件类型（md / pdf / docx / xlsx 等） */
  fileType?: string;
  /** 原始文件名称（file_name，与 docName/title 可能不同） */
  fileName?: string;
  /** 空间 ID（tenant_id，文档列表接口返回） */
  tenantId?: string;
}

/** 创建知识库请求参数 */
export interface ICreateKBParams {
  /** 知识库名称（必填） */
  name: string;
  /** 知识库描述 */
  description?: string;
  /** 知识库类型，默认 document */
  type?: 'document' | 'faq';
  /** 是否临时知识库 */
  is_temporary?: boolean;
  /** 分块配置 */
  chunking_config?: Record<string, unknown>;
  /** 图片处理配置 */
  image_processing_config?: Record<string, unknown> | null;
  /** Embedding 模型 ID（不传则由后端兜底默认值） */
  embedding_model_id?: string;
  /** 摘要模型 ID（不传则由后端兜底默认值） */
  summary_model_id?: string;
  /** VLM 配置 */
  vlm_config?: Record<string, unknown>;
  /** ASR 配置 */
  asr_config?: Record<string, unknown>;
  /** 存储提供者配置 */
  storage_provider_config?: { provider: string; };
  /** 存储凭证（兼容旧版） */
  storage_config?: Record<string, string>;
  /** 图谱抽取配置 */
  extract_config?: Record<string, unknown> | null;
  /** FAQ 配置 */
  faq_config?: Record<string, unknown> | null;
  /** 问题生成配置 */
  question_generation_config?: Record<string, unknown>;
  /** 绑定的向量存储 ID */
  vector_store_id?: string;
  /** Wiki 配置 */
  wiki_config?: Record<string, unknown>;
  /** 索引策略 */
  indexing_strategy?: Record<string, unknown>;
  /** 文档标题（创建 KB 时同时创建初始文档） */
  title?: string;
  /** 文档内容（创建 KB 时同时创建初始文档） */
  content?: string;
}

/** 创建文档请求参数 */
export interface ICreateDocumentParams {
  /** 文档标题 */
  title: string;
  /** 文档内容 */
  content: string;
  /** 发布状态，默认 publish */
  status?: string;
  /** 分类 ID */
  tag_id?: string;
}

/** 团队成员关系项（来自 GET /api/v1/auth/me → memberships） */
export interface IMembershipItem {
  /** 租户（团队）ID */
  tenant_id: number;
  /** 租户（团队）名称 */
  tenant_name?: string;
  /** 描述 */
  description?: string;
  /** 是否为公开空间 */
  is_public?: boolean;
  /** 用户在该团队中的角色 */
  role: string;
  /** 排序值 */
  sort_order?: number;
  /** 是否为 home tenant */
  is_home?: boolean;
  /** 是否可删除 */
  can_delete?: boolean;
  /** 知识库数量 */
  kb_count?: number;
  /** 文件数量 */
  file_count?: number;
}

/** 团队下拉选项 */
export interface ITeamOption {
  /** 团队 ID（字符串形式） */
  value: string;
  /** 团队展示名称 */
  text: string;
}

/** 加入知识库弹窗表单值 */
export interface IKnowledgeBaseAddFormValues {
  /** 空间类型 */
  space: 'personal' | 'team';
  /** 团队 ID（团队空间时必填） */
  teamId?: string;
  /** 目标知识库 ID（existing tab） */
  kbId?: string;
  /** 知识库名称（new tab） */
  kbName?: string;
  /** 知识库描述（new tab） */
  kbDesc?: string;
  /** 知识库类型，默认 document（new tab） */
  kbType?: 'document' | 'faq';
  /** 文档标题 */
  title: string;
  /** 文档内容 */
  content: string;
}

export interface IUseKnowledgeBaseListOptions {
  /** 是否发起请求，默认 true */
  ready?: boolean;
  /** 手动触发 */
  manual?: boolean;
  /** 列表搜索词；有值时额外带 keyword，cacheKey 与全量列表分开 */
  keyword?: string;
}

/** 判断是否为 AbortController / refreshDeps 切换导致的主动取消 */
const isRequestCanceled = (error: unknown): boolean => {
  const msg = (error as { msg?: string; })?.msg;
  return msg === 'canceled';
};

/** 构建 X-Tenant-ID 请求头（未传 tenantId 时不附 header） */
const buildTenantHeaders = (tenantId?: string): Record<string, string> | undefined => {
  if (!tenantId) return undefined;
  return { 'X-Tenant-ID': tenantId };
};

/** 从 KB 接口响应取错误文案 */
const extractKbApiError = (result: unknown): string | undefined => {
  const error = (result as { error?: unknown; })?.error;
  const text = typeof error === 'string' ? error : (error as { message?: string; })?.message;
  return typeof text === 'string' && text.trim() ? text.trim() : undefined;
};

export { extractKbApiError };

export type IKbCreateResult =
  | { ok: true; id: string; tenantId?: string; }
  | { ok: false; error?: string; };

export type IKbCreateDocumentResult =
  | { ok: true; kbId?: string; tenantId?: string; knowledgeId?: string; }
  | { ok: false; error?: string; };

interface ICreateDocumentResponseData {
  id?: string;
  knowledge_id?: string;
  knowledge_base_id?: string;
  tenant_id?: number | string;
}

const parseCreateDocumentResponse = (
  data?: ICreateDocumentResponseData,
): IKbCreateDocumentResult | null => {
  if (!data?.knowledge_base_id) return null;
  const knowledgeId = data.id ?? data.knowledge_id;
  return {
    ok: true,
    kbId: String(data.knowledge_base_id),
    tenantId: toTenantId(data.tenant_id),
    knowledgeId: knowledgeId ? String(knowledgeId) : undefined,
  };
};

const toTenantId = (value?: number | string): string | undefined =>
  value !== undefined && value !== null ? String(value) : undefined;

/** 弹窗内 KB 接口缓存时长（30min，与 @ 引用列表一致） */
const KB_ADD_MODAL_CACHE_MS = 1800000;

const KB_ADD_MODAL_MODELS_CACHE_KEY = 'KbAddModalDefaultModels';

interface IKbModelItem {
  id?: string;
  type?: string;
  is_default?: boolean;
}

/** 对齐 WeKnora useSimpleKbCreate：取默认 KnowledgeQA / Embedding 模型 */
const pickDefaultKbModelId = (
  models: IKbModelItem[],
  type: string,
): string | undefined => {
  const list = models.filter((item) => item.type === type);
  const target = list.find((item) => item.is_default) || list[0];
  return target?.id;
};

const fetchDefaultKbModelIds = async (): Promise<{
  embedding_model_id?: string;
  summary_model_id?: string;
}> => {
  const result = await http('/api/kb/v1/models', {
    method: 'GET',
    rawData: true,
    ignoreCode: true,
    errorHandler: true,
  });
  const { data } = result as { data?: IKbModelItem[]; };
  const models = Array.isArray(data) ? data : [];
  return {
    summary_model_id: pickDefaultKbModelId(models, 'KnowledgeQA'),
    embedding_model_id: pickDefaultKbModelId(models, 'Embedding'),
  };
};

/** 新建知识库默认模型（弹窗打开时请求一次，ahooks 缓存） */
export const useKbDefaultModels = (ready = true) => {
  const { data, loading } = useRequest(
    () => fetchDefaultKbModelIds(),
    {
      ready,
      cacheKey: KB_ADD_MODAL_MODELS_CACHE_KEY,
      staleTime: KB_ADD_MODAL_CACHE_MS,
      cacheTime: KB_ADD_MODAL_CACHE_MS,
    },
  );

  const isReady = Boolean(data?.summary_model_id && data?.embedding_model_id);

  return {
    loading,
    isReady,
    modelIds: isReady
      ? {
        summary_model_id: data!.summary_model_id!,
        embedding_model_id: data!.embedding_model_id!,
      }
      : null,
  };
};

/**
 * 获取知识库列表（@ / 按钮面板 / Home 预填 / 编辑态）
 * - GET /knowledge-bases?scope=user（可带 keyword）
 * - 全量与搜索分开实例化：全量常驻缓存，搜索仅在 keyword + ready 时请求，互不 mutate
 * - 文档按需：面板用 useInteractiveKbDocs，编辑用 useHistoryKbDocsRestore
 */
export const useKnowledgeBaseList = (
  options: IUseKnowledgeBaseListOptions = {},
) => {
  const { ready = true, manual = false, keyword } = options;

  const listRequestReady = manual ? true : ready;
  const trimmedKeyword = (keyword || '').trim();
  const isSearchRequest = Boolean(trimmedKeyword);

  const listParams = useMemo(() => {
    if (!trimmedKeyword) {
      return { scope: 'user' };
    }
    return { scope: 'user', keyword: trimmedKeyword };
  }, [trimmedKeyword]);

  const {
    data: rawResponse,
    loading: listLoading,
    error: listError,
    refresh: refreshList,
    mutate,
    cancelRequest,
    ...rest
  } = useRequestController({
    method: 'GET',
    url: '/api/kb/v1/knowledge-bases',
    manual,
    ready: listRequestReady,
    refreshDeps: listParams,
    cacheKey: getKnowledgeBaseListCacheKey(trimmedKeyword),
    staleTime: isSearchRequest ? 0 : 1800000,
    cacheTime: isSearchRequest ? 0 : 1800000,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
  });

  const kbList = useMemo(() => normalizeKbList(rawResponse), [rawResponse]);
  const hasError = Boolean(listError) && !isRequestCanceled(listError);

  return {
    ...rest,
    loading: listLoading,
    listLoading,
    error: hasError,
    kbList,
    mutate,
    refresh: refreshList,
    cancelRequest,
  };
};

/**
 * 创建知识库
 * @description 调用 POST /api/kb/v1/knowledge-bases，创建空知识库。
 * 高级配置（chunking / embedding 等）不传则由后端使用系统默认值兜底。
 * @param tenantId - 可选，团队 ID，传递 X-Tenant-ID header
 * @returns createKb - 创建函数，成功返回 { ok: true, id, tenantId? }，失败返回 { ok: false, error? }
 * @returns loading - 请求中状态
 */
export const useCreateKnowledgeBase = (tenantId?: string) => {
  const [loading, setLoading] = useState(false);

  const createKb = useCallback(
    async (values: ICreateKBParams): Promise<IKbCreateResult> => {
      setLoading(true);
      try {
        const result = await http('/api/kb/v1/knowledge-bases', {
          method: 'POST',
          data: values,
          rawData: true,
          ignoreCode: true,
          errorHandler: true,
          headers: buildTenantHeaders(tenantId),
        });
        const { data } = result as { data?: { id?: string; tenant_id?: number | string; }; };
        if (data?.id) {
          return {
            ok: true,
            id: String(data.id),
            tenantId: toTenantId(data.tenant_id),
          };
        }
        return { ok: false, error: extractKbApiError(result) };
      } catch (err) {
        const { data } = err as { data?: { id?: string; tenant_id?: number | string; }; };
        if (data?.id) {
          return {
            ok: true,
            id: String(data.id),
            tenantId: toTenantId(data.tenant_id),
          };
        }
        return { ok: false, error: extractKbApiError(err) };
      } finally {
        setLoading(false);
      }
    },
    [tenantId],
  );

  return {
    createKb,
    loading,
  };
};

/**
 * 创建文档到指定知识库
 * @description 调用 POST /api/kb/v1/knowledge-bases/{kbId}/knowledge/manual
 * @param tenantId - 可选，团队 ID，传递 X-Tenant-ID header
 * @returns createDocument - 创建函数，成功返回 { ok: true }，失败返回 { ok: false, error? }
 * @returns loading - 请求中状态
 */
export const useCreateKnowledgeDocument = (tenantId?: string) => {
  const createDocument = useCallback(
    async (
      kbId: string,
      docParams: ICreateDocumentParams,
      docTenantId?: string,
    ): Promise<IKbCreateDocumentResult> => {
      try {
        const result = await http(`/api/kb/v1/knowledge-bases/${kbId}/knowledge/manual`, {
          method: 'POST',
          data: docParams,
          rawData: true,
          ignoreCode: true,
          errorHandler: true,
          headers: buildTenantHeaders(docTenantId ?? tenantId),
        });
        const { data } = result as { data?: ICreateDocumentResponseData; };
        const parsed = parseCreateDocumentResponse(data);
        if (parsed) return parsed;
        return { ok: false, error: extractKbApiError(result) };
      } catch (err) {
        const { data } = err as { data?: ICreateDocumentResponseData; };
        const parsed = parseCreateDocumentResponse(data);
        if (parsed) return parsed;
        return { ok: false, error: extractKbApiError(err) };
      }
    },
    [tenantId],
  );

  return { createDocument, loading: false };
};
